import * as yup from 'yup';

// Common validation rules for the application
export const commonRules = {
  // Required field
  required: (fieldName = 'Este campo') =>
    yup.mixed().required(`${fieldName} é obrigatório`),

  // String with minimum length
  stringMinLength: (min, fieldName = 'Este campo') =>
    yup.string().min(min, `${fieldName} deve ter pelo menos ${min} caracteres`),

  // String with maximum length
  stringMaxLength: (max, fieldName = 'Este campo') =>
    yup.string().max(max, `${fieldName} deve ter no máximo ${max} caracteres`),

  // Email validation
  email: (fieldName = 'Email') =>
    yup.string().email(`${fieldName} deve ser um email válido`),

  // Phone number validation (Portuguese format)
  phoneNumber: (fieldName = 'Telefone') =>
    yup.string()
      .matches(
        /^(\+351)?[0-9\s-()]{9,15}$/,
        `${fieldName} deve ser um número válido`
      ),

  // Numeric validation
  numeric: (fieldName = 'Este campo') =>
    yup.number().typeError(`${fieldName} deve ser um número`),

  // Non-negative number (0 or greater)
  positiveNumber: (fieldName = 'Este campo') =>
    yup.number()
      .min(0, `${fieldName} deve ser 0 ou maior`)
      .typeError(`${fieldName} deve ser um número`),

  // Strictly positive number (greater than 0)
  strictlyPositiveNumber: (fieldName = 'Este campo') =>
    yup.number()
      .positive(`${fieldName} deve ser um número positivo`)
      .typeError(`${fieldName} deve ser um número`),

  // Integer validation
  integer: (fieldName = 'Este campo') =>
    yup.number()
      .integer(`${fieldName} deve ser um número inteiro`)
      .typeError(`${fieldName} deve ser um número`),

  // Positive integer greater than or equal to 1
  positiveInteger: (fieldName = 'Este campo') =>
    yup.number()
      .integer(`${fieldName} deve ser um número inteiro`)
      .min(1, `${fieldName} deve ser maior ou igual a 1`)
      .typeError(`${fieldName} deve ser um número`),

  // Date validation
  date: (fieldName = 'Data') =>
    yup.date().typeError(`${fieldName} deve ser uma data válida`),

  // Date not in the past
  futureDate: (fieldName = 'Data') =>
    yup.date()
      .min(new Date(), `${fieldName} não pode ser no passado`)
      .typeError(`${fieldName} deve ser uma data válida`),

  // Select validation (for dropdowns)
  selectRequired: (fieldName = 'Seleção') =>
    yup.mixed()
      .test('not-zero', `${fieldName} é obrigatório`, (value) => {
        return value != null && value !== 0 && value !== '' && value !== undefined;
      }),

  // Object with id validation (for select components)
  objectWithId: (fieldName = 'Seleção') =>
    yup.object()
      .shape({
        id: yup.number().min(1, `${fieldName} é obrigatório`)
      })
      .nullable()
      .test('object-required', `${fieldName} é obrigatório`, (value) => {
        return value && value.id && value.id > 0;
      }),

  // Text area with minimum length
  textAreaMinLength: (min, fieldName = 'Descrição') =>
    yup.string()
      .min(min, `${fieldName} deve ter pelo menos ${min} caracteres`)
      .nullable(),

  // Optional string
  optionalString: () => yup.string().nullable(),

  // Optional number
  optionalNumber: () => yup.number().nullable(),

  // Discount percentage validation (0-100%)
  discountPercentage: (fieldName = 'Desconto') =>
    yup.number()
      .nullable()
      .transform((value, originalValue) => {
        // Handle empty string or null/undefined
        if (originalValue === '' || originalValue == null) return null;
        // Convert to number
        const num = parseFloat(originalValue);
        return isNaN(num) ? null : num;
      })
      .min(0, `${fieldName} deve ser maior ou igual a 0`)
      .max(100, `${fieldName} deve ser menor ou igual a 100`),

  // Custom validation for Portuguese NIF (Tax ID)
  nif: (fieldName = 'NIF') =>
    yup.string()
      .matches(/^[0-9]{9}$/, `${fieldName} deve ter 9 dígitos`)
      .test('valid-nif', `${fieldName} inválido`, (value) => {
        if (!value) return true; // Allow empty if not required

        // Basic NIF validation algorithm
        const digits = value.split('').map(Number);
        const checkDigit = digits[8];
        const sum = digits.slice(0, 8).reduce((acc, digit, index) => {
          return acc + digit * (9 - index);
        }, 0);
        const remainder = sum % 11;
        const calculatedCheckDigit = remainder < 2 ? 0 : 11 - remainder;

        return calculatedCheckDigit === checkDigit;
      }),

  // Postal code validation (Portuguese format)
  postalCode: (fieldName = 'Código Postal') =>
    yup.string()
      .matches(/^[0-9]{4}-[0-9]{3}$/, `${fieldName} deve estar no formato 0000-000`)
};

// Helper function to create conditional validation
export const conditionalValidation = (condition, schema) => {
  return yup.mixed().when([], {
    is: () => condition,
    then: () => schema,
    otherwise: () => yup.mixed().nullable()
  });
};

export default commonRules;